import {
  Children,
  CSSProperties,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TailSpin } from 'react-loader-spinner';
import {
  BUTTON_VARIANT,
  IButtonProps,
} from 'types/components/button/button.interface';
import DropdownMenu from '../dropdown-menu/DropdownMenu';
import './Button.scss';
import { ButtonIcon, ButtonText, ButtonDescription } from './modules';
import ButtonLabel from './modules/Label/ButtonLabel';
import {
  ButtonDescriptionPosition,
  IButtonDescriptionProps,
} from './modules/Description/ButtonDescription';

const TOOLTIP_OFFSET_PX = 4;
const TOOLTIP_Z_INDEX = 9999;

const isButtonDescription = (
  child: ReactNode
): child is ReactElement<IButtonDescriptionProps> =>
  isValidElement(child) && child.type === ButtonDescription;

const isVisibleDropdownMenu = (node: ReactNode): boolean =>
  isValidElement(node) &&
  node.type === DropdownMenu &&
  !!node.props.visible;

const splitButtonChildren = (children: ReactNode) => {
  const buttonChildren: ReactNode[] = [];
  const descriptionChildren: ReactNode[] = [];

  Children.forEach(children, child => {
    if (isButtonDescription(child)) {
      descriptionChildren.push(child);
      return;
    }

    buttonChildren.push(child);
  });

  return { buttonChildren, descriptionChildren };
};

const getTooltipStyle = (
  rect: DOMRect,
  position: ButtonDescriptionPosition
): CSSProperties => {
  const centerX = rect.left + rect.width / 2;

  if (position === 'top') {
    return {
      position: 'fixed',
      top: rect.top - TOOLTIP_OFFSET_PX,
      left: centerX,
      transform: 'translate(-50%, -100%)',
      zIndex: TOOLTIP_Z_INDEX,
    };
  }

  return {
    position: 'fixed',
    top: rect.bottom + TOOLTIP_OFFSET_PX,
    left: centerX,
    transform: 'translateX(-50%)',
    zIndex: TOOLTIP_Z_INDEX,
  };
};

const Button = ({
  isLoading,
  children,
  variant = BUTTON_VARIANT.default,
  isActive,
  fullWidth,
  onlyIcon,
  outsideChildren,
  style,
  buttonStyle,
  isClear,
  className,
  wrapperClassNames,
  size = 'default',
  type = 'button',
  disabled,
  hideDescription = false,
  ...props
}: IButtonProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isTooltipSuppressed, setIsTooltipSuppressed] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>();

  const { buttonChildren, descriptionChildren } = splitButtonChildren(children);
  const descriptionElement = descriptionChildren.find(isButtonDescription);
  const descriptionProps = descriptionElement?.props;
  const isDropdownVisible = isVisibleDropdownMenu(outsideChildren);
  const canShowTooltip =
    !disabled &&
    !isLoading &&
    !hideDescription &&
    !isDropdownVisible &&
    !isTooltipSuppressed &&
    !!descriptionProps;

  const updateTooltipPosition = useCallback(() => {
    if (!wrapperRef.current || !descriptionProps) {
      return;
    }

    setTooltipStyle(
      getTooltipStyle(
        wrapperRef.current.getBoundingClientRect(),
        descriptionProps.position ?? 'bottom'
      )
    );
  }, [descriptionProps]);

  const showTooltip = useCallback(() => {
    if (!canShowTooltip) {
      return;
    }

    updateTooltipPosition();
    setIsHovered(true);
  }, [canShowTooltip, updateTooltipPosition]);

  const hideTooltip = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const nextTarget = event.relatedTarget;
      if (
        nextTarget instanceof Node &&
        wrapperRef.current?.contains(nextTarget)
      ) {
        return;
      }

      setIsTooltipSuppressed(false);
      hideTooltip();
    },
    [hideTooltip]
  );

  useEffect(() => {
    if (hideDescription || isDropdownVisible) {
      setIsTooltipSuppressed(true);
      hideTooltip();
    }
  }, [hideDescription, isDropdownVisible, hideTooltip]);

  useEffect(() => {
    if (!isHovered) {
      return;
    }

    const handlePositionUpdate = () => updateTooltipPosition();

    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);

    return () => {
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [isHovered, updateTooltipPosition]);

  const baseClasses = 'button';
  const variantClasses = ` ${BUTTON_VARIANT[variant]}`;
  const activeClasses = `${isActive ? ' active' : ''}`;
  const onlyIconClasses = `${onlyIcon ? ' only-icon' : ''}`;
  const fullwidthClasses = fullWidth ? ' full-width' : '';
  const loadingClasses = isLoading ? ' loading' : '';
  const clearClasses = isClear ? ' clear' : '';

  return (
    <div
      ref={wrapperRef}
      className={`button-wrapper${fullwidthClasses}${loadingClasses} ${wrapperClassNames ?? ''}`}
      style={style}
      onMouseEnter={showTooltip}
      onMouseLeave={handleMouseLeave}>
      <TailSpin wrapperClass='button-spinner' />
      <button
        style={buttonStyle}
        type={type}
        disabled={disabled}
        {...props}
        onClick={e => {
          setIsTooltipSuppressed(true);
          hideTooltip();
          if (!isLoading && props.onClick) {
            props.onClick(e);
          }
        }}
        className={`${baseClasses}${variantClasses}${activeClasses}${onlyIconClasses}${clearClasses} ${size} ${className ?? ''}`}>
        {buttonChildren}
      </button>
      {isHovered &&
        canShowTooltip &&
        tooltipStyle &&
        createPortal(
          <div
            className={`button-description button-description--${descriptionProps?.position ?? 'bottom'}`}
            style={tooltipStyle}>
            <p>{descriptionProps?.description}</p>
          </div>,
          document.body
        )}
      {outsideChildren}
    </div>
  );
};

Button.Icon = ButtonIcon;
Button.Text = ButtonText;
Button.Description = ButtonDescription;
Button.Label = ButtonLabel;

export default Button;
